import { z } from "zod";
import { idSchema, isoDateTimeSchema } from "../common/primitives.js";
import { workspaceParamsSchema } from "../workspaces/workspace.schemas.js";

/** Fixed palette: the UI maps each value to theme-aware classes, so tags never carry raw colors. */
export const TAG_COLORS = [
  "GRAY",
  "RED",
  "ORANGE",
  "YELLOW",
  "GREEN",
  "TEAL",
  "BLUE",
  "PURPLE",
  "PINK",
] as const;
export type TagColor = (typeof TAG_COLORS)[number];
export const tagColorSchema = z.enum(TAG_COLORS);

/** The most tags one task can carry. */
export const MAX_TAGS_PER_TASK = 20;

export const tagSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  name: z.string(),
  color: tagColorSchema,
  createdAt: isoDateTimeSchema,
});
export type Tag = z.infer<typeof tagSchema>;

/** A tag as shown on a task. */
export const taskTagSchema = tagSchema.pick({ id: true, name: true, color: true });
export type TaskTag = z.infer<typeof taskTagSchema>;

const tagNameSchema = z
  .string()
  .trim()
  .min(1, "Name is required")
  .max(40, "Name must be at most 40 characters");

export const createTagSchema = z.object({
  name: tagNameSchema,
  color: tagColorSchema.default("GRAY"),
});
export type CreateTagInput = z.input<typeof createTagSchema>;

export const updateTagSchema = z
  .object({ name: tagNameSchema.optional(), color: tagColorSchema.optional() })
  .refine((value) => Object.values(value).some((v) => v !== undefined), {
    error: "Provide at least one field to update",
  });
export type UpdateTagInput = z.infer<typeof updateTagSchema>;

export const tagParamsSchema = workspaceParamsSchema.extend({ tagId: idSchema });

export const tagResponseSchema = z.object({ tag: tagSchema });
export type TagResponse = z.infer<typeof tagResponseSchema>;

/** Every tag of the workspace, by name. */
export const tagsResponseSchema = z.object({ tags: z.array(tagSchema) });
export type TagsResponse = z.infer<typeof tagsResponseSchema>;
